/**
 * PopiPlay RGS Provider Configuration — 3 Orbs of Zeus
 *
 * API uses 0-indexed symbol IDs matching the symbol list in the API contract.
 * SYMBOL_MAP is identity (0→0, 1→1, ..., 25→25).
 * See docs/api-contract-3-orbs-of-zeus.md for full symbol list.
 */

export const PROVIDER_CONFIG = {
  // Provider identification
  PROVIDER_NAME: "PopiPlay",

  // Symbol mapping: API symbol ID → game symbol ID (identity — 0-indexed)
  SYMBOL_MAP: {
    0: 0,   // w0 (wild)
    1: 1,   // m1 (high pay)
    2: 2,   // m2 (mid pay)
    3: 3,   // m3 (mid pay)
    4: 4,   // m4 (mid pay)
    5: 5,   // l1 (low pay)
    6: 6,   // l2 (low pay)
    7: 7,   // l3 (low pay)
    8: 8,   // l4 (low pay)
    9: 9,   // l5 (low pay)
    10: 10, // sc (coin — blank coin on base game reels)
    11: 11, // scatter (bonus trigger — unused currently)
    12: 12, // cd (Double Zeus — green)
    13: 13, // cm (Mystery Zeus — red)
    14: 14, // cc (Collect Zeus — blue)
    15: 15, // blank (empty cell during respins)
    16: 16, // locked_blank (occupied-but-empty during respins)
    17: 17, // c_mini (jackpot mini)
    18: 18, // c_minor (jackpot minor)
    19: 19, // c_major (jackpot major)
    20: 20, // cd_mult (Double Zeus after feature applied)
    21: 21, // cm_minor (Mystery Zeus revealed as Minor)
    22: 22, // cm_mini (Mystery Zeus revealed as Mini)
    23: 23, // cm_major (Mystery Zeus revealed as Major)
    24: 24, // cd_empty (Double Zeus pre-value)
    25: 25, // cc_empty (Collect Zeus pre-value)
  },

  // Wild symbol game ID (used to skip wilds when deriving winning symbol from screen)
  WILD_SYMBOL_ID: 0,

  // Scatter symbol game IDs — c0 (coin placeholder) triggers Hold & Win
  SCATTER_SYMBOL_IDS: [11],

  // Win thresholds (multiplier of bet size) — animated win tiers per GDD §17.1
  // GDD names → spine skin names (skins drive the visual, animations are shared in/idle/out):
  //   Big Win (20x)             → big_win
  //   Mega Win (40x)            → mega_win
  //   Super Mega Win (60x)      → super_mega_win
  //   Max Win (10,000x)         → max_win
  WIN_THRESHOLDS: {
    BIG_WIN_THRESHOLD: 20,              // 20x (Big Win)
    MEGA_WIN_THRESHOLD: 40,             // 40x (Mega Win)
    SUPER_MEGA_WIN_THRESHOLD: 60,       // 60x (Super Mega Win)
    MAX_WIN_THRESHOLD: 10000,           // 10,000x (Max Win cap per GDD §18)
  },

  // Max win multiplier (bet × this value = max possible win)
  // Sourced from PopiPlay loader (window.__OPTIONS__.math.max_multiplier)
  MAX_WIN_MULTIPLIER: window.__OPTIONS__?.math?.max_multiplier || 10000,

  // Win type constants (mapped to spine skin names)
  WIN_TYPES: {
    REGULAR_WIN: "regular_win",
    BIG_WIN: "big_win",
    MEGA_WIN: "mega_win",
    SUPER_MEGA_WIN: "super_mega_win",
    MAX_WIN: "max_win",
  },

  // Which wins trigger animated celebrations (full-screen win animation)
  ANIMATED_WIN_TYPES: [
    "big_win",
    "mega_win",
    "super_mega_win",
    "max_win",
  ],

  // Error code mappings - PopiPlay API codes → normalized codes (WarningPopup understands these)
  // Platform API v1.0.2 ranges: 1xx=auth, 2xx=action, 3xx=money, 4xx=bet limits
  ERROR_CODES: {
    // All codes pass through as-is — translations-common.json has messages for each
    101: 101, // player_unauthenticated
    102: 102, // player_disabled
    103: 103, // ip_not_allowed
    104: 104, // game_locked
    105: 105, // request_timeout
    201: 201, // unknown_action
    202: 202, // unavailable_action
    203: 203, // invalid_options
    204: 204, // unknown_exception
    205: 205, // bonus_spin_expected (respin errors in Zeus)
    206: 206, // regular_spin_expected / free_rounds_ended
    301: 301, // not_enough_money
    302: 302, // player_limit_exceeded
    303: 303, // max_bet_exceeded
    401: 401, // exceed_bet_limits
    600: 600, // not_synced_game
    700: 700, // message (reality check)
  },
};
